import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';

const ExceptionManagement = ({ filters }) => {
  const [loading, setLoading] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedException, setSelectedException] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0
  });

  // Form states
  const [formData, setFormData] = useState({
    asset_id: '',
    exception_type: 'other',
    severity: 'medium',
    description: '',
    assigned_to: ''
  });

  const [resolveData, setResolveData] = useState({
    resolution_notes: '',
    status: 'resolved'
  });

  const exceptionTypes = [
    { value: 'missing_asset', label: 'Missing Asset' },
    { value: 'location_mismatch', label: 'Location Mismatch' },
    { value: 'condition_issue', label: 'Condition Issue' },
    { value: 'duplicate_entry', label: 'Duplicate Entry' },
    { value: 'stolen_asset', label: 'Stolen Asset' },
    { value: 'damaged_asset', label: 'Damaged Asset' },
    { value: 'unauthorized_removal', label: 'Unauthorized Removal' },
    { value: 'other', label: 'Other' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'investigating', label: 'Investigating' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
    { value: 'escalated', label: 'Escalated' }
  ];

  const loadExceptions = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        per_page: pagination.per_page,
        ...filters
      };

      const response = await axiosInstance.get('/audit/exceptions', { params });
      
      if (response.data.success) {
        setExceptions(response.data.data.data || []);
        setPagination({
          current_page: response.data.data.current_page || 1,
          per_page: response.data.data.per_page || 20,
          total: response.data.data.total || 0
        });
      } else {
        toast.error('Failed to load exceptions');
      }
    } catch (error) {
      console.error('Error loading exceptions:', error);
      toast.error('Failed to load exceptions');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  useEffect(() => {
    loadExceptions();
  }, [loadExceptions]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = (event) => {
      const newFilters = event.detail || {};
      if (Object.keys(newFilters).length > 0) {
        loadExceptions(1);
      } else {
        loadExceptions(pagination.current_page);
      }
    };
    window.addEventListener('refreshAuditView', handleRefresh);
    return () => window.removeEventListener('refreshAuditView', handleRefresh);
  }, [loadExceptions, pagination.current_page]);

  const handleCreateException = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/audit/exceptions', formData);
      
      if (response.data.success) {
        toast.success('Exception created successfully');
        setShowCreateModal(false);
        setFormData({
          asset_id: '',
          exception_type: 'other',
          severity: 'medium',
          description: '',
          assigned_to: ''
        });
        loadExceptions();
      } else {
        toast.error(response.data.message || 'Failed to create exception');
      }
    } catch (error) {
      console.error('Error creating exception:', error);
      toast.error(error.response?.data?.message || 'Failed to create exception');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveException = async () => {
    if (!selectedException) return;

    try {
      setLoading(true);
      const response = await axiosInstance.put(
        `/audit/exceptions/${selectedException.id}/resolve`,
        resolveData
      );
      
      if (response.data.success) {
        toast.success('Exception resolved successfully');
        setShowResolveModal(false);
        setSelectedException(null);
        setResolveData({
          resolution_notes: '',
          status: 'resolved'
        });
        loadExceptions();
      } else {
        toast.error(response.data.message || 'Failed to resolve exception');
      }
    } catch (error) {
      console.error('Error resolving exception:', error);
      toast.error(error.response?.data?.message || 'Failed to resolve exception');
    } finally {
      setLoading(false);
    }
  };

  const handleEscalateException = async (exception) => {
    try {
      setLoading(true);
      const response = await axiosInstance.put(`/audit/exceptions/${exception.id}/escalate`, {
        escalated_to: prompt('Enter the name or email to escalate to:', '')
      });
      
      if (response.data.success) {
        toast.success('Exception escalated successfully');
        loadExceptions();
      } else {
        toast.error(response.data.message || 'Failed to escalate exception');
      }
    } catch (error) {
      console.error('Error escalating exception:', error);
      toast.error(error.response?.data?.message || 'Failed to escalate exception');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error text-error-foreground';
      case 'high':
        return 'bg-warning text-warning-foreground';
      case 'medium':
        return 'bg-accent text-accent-foreground';
      case 'low':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-warning text-warning-foreground';
      case 'investigating':
        return 'bg-accent text-accent-foreground';
      case 'resolved':
        return 'bg-success text-success-foreground';
      case 'closed':
        return 'bg-muted text-muted-foreground';
      case 'escalated':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Exception Management</h3>
          <p className="text-sm text-muted-foreground">
            {pagination.total} exceptions reported
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowCreateModal(true)}
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Report Exception
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Exceptions List */}
          <div className="space-y-4">
            {exceptions.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
                <p className="text-muted-foreground">No exceptions found</p>
                <p className="text-sm text-muted-foreground mt-1">All assets are verified</p>
              </div>
            ) : (
              exceptions.map((exception) => (
                <div key={exception.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 flex-wrap gap-2 mb-2">
                        <h4 className="font-medium text-card-foreground">{exception.exception_id}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(exception.severity)}`}>
                          {exception.severity}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(exception.status)}`}>
                          {exception.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{exception.description}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Asset ID</p>
                          <p className="font-medium">{exception.asset_id || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Type</p>
                          <p className="font-medium">{exception.exception_type?.replace('_', ' ') || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Location</p>
                          <p className="font-medium">{exception.location || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Reported</p>
                          <p className="font-medium">{new Date(exception.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {exception.assigned_to && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Assigned to: {exception.assigned_to}
                        </p>
                      )}
                      {exception.escalated_to && (
                        <p className="text-sm text-warning mt-1">
                          Escalated to: {exception.escalated_to}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {exception.status !== 'resolved' && exception.status !== 'closed' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedException(exception);
                              setShowResolveModal(true);
                            }}
                          >
                            <Icon name="Check" size={14} className="mr-1" />
                            Resolve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEscalateException(exception)}
                          >
                            <Icon name="ArrowUp" size={14} className="mr-1" />
                            Escalate
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.total > pagination.per_page && (
            <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page === 1}
                  onClick={() => loadExceptions(pagination.current_page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {pagination.current_page} of {Math.ceil(pagination.total / pagination.per_page)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)}
                  onClick={() => loadExceptions(pagination.current_page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Exception Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-card-foreground">Report Exception</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateModal(false)}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <Input
                label="Asset ID"
                type="text"
                placeholder="Enter asset ID"
                value={formData.asset_id}
                onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
              />

              <Select
                label="Exception Type"
                options={exceptionTypes}
                value={formData.exception_type}
                onChange={(value) => setFormData({ ...formData, exception_type: value })}
              />

              <Select
                label="Severity"
                options={severityOptions}
                value={formData.severity}
                onChange={(value) => setFormData({ ...formData, severity: value })}
              />

              <Input
                label="Description"
                type="textarea"
                placeholder="Describe the exception..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />

              <Input
                label="Assign To (Optional)"
                type="text"
                placeholder="Enter assignee name"
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              />
            </div>

            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateException}
                loading={loading}
              >
                Report Exception
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Exception Modal */}
      {showResolveModal && selectedException && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-lg">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-card-foreground">Resolve Exception</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowResolveModal(false);
                    setSelectedException(null);
                  }}
                  iconName="X"
                />
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Exception:</strong> {selectedException.exception_id}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Description:</strong> {selectedException.description}
                </p>
              </div>

              <Input
                label="Resolution Notes"
                type="textarea"
                placeholder="Describe how the exception was resolved..."
                value={resolveData.resolution_notes}
                onChange={(e) => setResolveData({ ...resolveData, resolution_notes: e.target.value })}
                rows={4}
              />

              <Select
                label="Resolution Status"
                options={statusOptions.filter(s => s.value === 'resolved' || s.value === 'closed')}
                value={resolveData.status}
                onChange={(value) => setResolveData({ ...resolveData, status: value })}
              />
            </div>

            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResolveModal(false);
                  setSelectedException(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleResolveException}
                loading={loading}
              >
                Resolve Exception
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExceptionManagement;